import React, { useState, useMemo } from "react";
import { Link } from "react-router-dom";
import {
  IconSearch,
  IconChartArea,
  IconChartBar,
  IconBuildingBank,
  IconScale,
  IconReportMoney, IconCashBanknote, IconReportAnalytics,
  IconReceipt,
  IconDiscount2,
  IconTrashOff,
  IconCalculator,
  IconBed,
  IconUsers,
  IconBrush,
  IconHanger,
  IconTools,
  IconBell,
  IconShieldCheck,
  IconBriefcase,
  IconBuildingWarehouse,
  IconShoppingBag,
  IconCoin,
  IconArrowRight,
  IconLayoutGrid,
  IconFolder
} from "@tabler/icons-react";
import { iconStroke } from "../config/config";
import { useTheme } from "../contexts/ThemeContext";

export default function ReportsDirectoryGrid() {
  const { theme } = useTheme();
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedCategory, setSelectedCategory] = useState("all");

  const reportsList = [
    // 1. Hotel & Operations
    {
      id: "front-office",
      title: "Front Office Reports",
      description: "Arrivals, departures, stayovers, and daily guest check-in register.",
      path: "/dashboard/reports/front-office",
      category: "hotel",
      categoryName: "Hotel & PMS",
      icon: <IconBed stroke={iconStroke} size={24} />,
      color: "from-blue-500/10 to-indigo-500/10 text-blue-600 dark:text-blue-400 border-blue-200 dark:border-blue-900/50",
      badgeColor: "bg-blue-100 text-blue-800 dark:bg-blue-950/60 dark:text-blue-300",
    },
    {
      id: "occupancy",
      title: "Occupancy Reports",
      description: "Room occupancy rates, RevPAR, ADR, and capacity utilization analytics.",
      path: "/dashboard/reports/occupancy",
      category: "hotel",
      categoryName: "Hotel & PMS",
      icon: <IconChartArea stroke={iconStroke} size={24} />,
      color: "from-indigo-500/10 to-violet-500/10 text-indigo-600 dark:text-indigo-400 border-indigo-200 dark:border-indigo-900/50",
      badgeColor: "bg-indigo-100 text-indigo-800 dark:bg-indigo-950/60 dark:text-indigo-300",
    },
    {
      id: "guest",
      title: "Guest Reports",
      description: "Guest directory, stay history, nationalities, and VIP guest profiles.",
      path: "/dashboard/reports/guest",
      category: "hotel",
      categoryName: "Hotel & PMS",
      icon: <IconUsers stroke={iconStroke} size={24} />,
      color: "from-sky-500/10 to-cyan-500/10 text-sky-600 dark:text-sky-400 border-sky-200 dark:border-sky-900/50",
      badgeColor: "bg-sky-100 text-sky-800 dark:bg-sky-950/60 dark:text-sky-300",
    },
    {
      id: "housekeeping",
      title: "Housekeeping Reports",
      description: "Room cleaning status, attendant task allocations, and linen readiness.",
      path: "/dashboard/reports/housekeeping",
      category: "hotel",
      categoryName: "Hotel & PMS",
      icon: <IconBrush stroke={iconStroke} size={24} />,
      color: "from-teal-500/10 to-emerald-500/10 text-teal-600 dark:text-teal-400 border-teal-200 dark:border-teal-900/50",
      badgeColor: "bg-teal-100 text-teal-800 dark:bg-teal-950/60 dark:text-teal-300",
    },
    {
      id: "laundry",
      title: "Laundry & Linen Reports",
      description: "Guest wash orders, in-house linen cycle, deliveries, and laundry revenue.",
      path: "/dashboard/reports/laundry",
      category: "hotel",
      categoryName: "Hotel & PMS",
      icon: <IconHanger stroke={iconStroke} size={24} />,
      color: "from-cyan-500/10 to-blue-500/10 text-cyan-600 dark:text-cyan-400 border-cyan-200 dark:border-cyan-900/50",
      badgeColor: "bg-cyan-100 text-cyan-800 dark:bg-cyan-950/60 dark:text-cyan-300",
    },
    {
      id: "guest-service",
      title: "Guest Service Reports",
      description: "Room service orders, concierge requests, amenities, and fulfillment time.",
      path: "/dashboard/reports/guest-service",
      category: "hotel",
      categoryName: "Hotel & PMS",
      icon: <IconBell stroke={iconStroke} size={24} />,
      color: "from-amber-500/10 to-yellow-500/10 text-amber-600 dark:text-amber-400 border-amber-200 dark:border-amber-900/50",
      badgeColor: "bg-amber-100 text-amber-800 dark:bg-amber-950/60 dark:text-amber-300",
    },
    {
      id: "maintenance",
      title: "Maintenance Reports",
      description: "Room maintenance tickets, equipment repair logs, and asset downtime.",
      path: "/dashboard/reports/maintenance",
      category: "hotel",
      categoryName: "Hotel & PMS",
      icon: <IconTools stroke={iconStroke} size={24} />,
      color: "from-stone-500/10 to-zinc-500/10 text-stone-600 dark:text-stone-400 border-stone-200 dark:border-stone-800",
      badgeColor: "bg-stone-100 text-stone-800 dark:bg-stone-900 dark:text-stone-300",
    },

    // 2. Sales, Kitchen & F&B
    {
      id: "revenue",
      title: "Revenue Reports",
      description: "Gross revenue, tax, service charges, discounts, and payment methods.",
      path: "/dashboard/reports/revenue",
      category: "sales",
      categoryName: "Sales & F&B",
      icon: <IconCoin stroke={iconStroke} size={24} />,
      color: "from-emerald-500/10 to-green-500/10 text-emerald-600 dark:text-emerald-400 border-emerald-200 dark:border-emerald-900/50",
      badgeColor: "bg-emerald-100 text-emerald-800 dark:bg-emerald-950/60 dark:text-emerald-300",
    },
    {
      id: "fb",
      title: "F&B Reports",
      description: "Food and beverage sales, category trends, dining vs takeaway metrics.",
      path: "/dashboard/reports/fb",
      category: "sales",
      categoryName: "Sales & F&B",
      icon: <IconChartBar stroke={iconStroke} size={24} />,
      color: "from-green-500/10 to-teal-500/10 text-green-600 dark:text-green-400 border-green-200 dark:border-green-900/50",
      badgeColor: "bg-green-100 text-green-800 dark:bg-green-950/60 dark:text-green-300",
    },
    {
      id: "kot-report",
      title: "KOT Report",
      description: "Kitchen Order Tickets by waiter, preparation time, and items status.",
      path: "/dashboard/reports/kot-report",
      category: "sales",
      categoryName: "Sales & F&B",
      icon: <IconReceipt stroke={iconStroke} size={24} />,
      color: "from-amber-500/10 to-orange-500/10 text-amber-600 dark:text-amber-400 border-amber-200 dark:border-amber-900/50",
      badgeColor: "bg-amber-100 text-amber-800 dark:bg-amber-950/60 dark:text-amber-300",
    },
    {
      id: "order-deletion-report",
      title: "Order Deletion Report",
      description: "Audit trail of deleted or cancelled orders, reasons, and responsible staff.",
      path: "/dashboard/reports/order-deletion-report",
      category: "sales",
      categoryName: "Sales & F&B",
      icon: <IconTrashOff stroke={iconStroke} size={24} />,
      color: "from-red-500/10 to-rose-500/10 text-red-600 dark:text-red-400 border-red-200 dark:border-red-900/50",
      badgeColor: "bg-red-100 text-red-800 dark:bg-red-950/60 dark:text-red-300",
    },
    {
      id: "discounts-deductions",
      title: "Discounts & Round Off",
      description: "Manual discounts, complementary items, promotions, and round-off deductions.",
      path: "/dashboard/reports/discounts-deductions",
      category: "sales",
      categoryName: "Sales & F&B",
      icon: <IconDiscount2 stroke={iconStroke} size={24} />,
      color: "from-pink-500/10 to-rose-500/10 text-pink-600 dark:text-pink-400 border-pink-200 dark:border-pink-900/50",
      badgeColor: "bg-pink-100 text-pink-800 dark:bg-pink-950/60 dark:text-pink-300",
    },

    // 3. Inventory & Supply
    {
      id: "inventory",
      title: "Inventory Reports",
      description: "Stock levels, ingredient consumption, low stock alerts, and valuation.",
      path: "/dashboard/reports/inventory",
      category: "inventory",
      categoryName: "Inventory",
      icon: <IconBuildingWarehouse stroke={iconStroke} size={24} />,
      color: "from-violet-500/10 to-purple-500/10 text-violet-600 dark:text-violet-400 border-violet-200 dark:border-violet-900/50",
      badgeColor: "bg-violet-100 text-violet-800 dark:bg-violet-950/60 dark:text-violet-300",
    },
    {
      id: "purchase",
      title: "Purchase Reports",
      description: "Supplier purchase orders, GRNs, item unit costs, and accounts payable.",
      path: "/dashboard/reports/purchase",
      category: "inventory",
      categoryName: "Inventory",
      icon: <IconShoppingBag stroke={iconStroke} size={24} />,
      color: "from-purple-500/10 to-fuchsia-500/10 text-purple-600 dark:text-purple-400 border-purple-200 dark:border-purple-900/50",
      badgeColor: "bg-purple-100 text-purple-800 dark:bg-purple-950/60 dark:text-purple-300",
    },

    // 4. Financial & Audit (including the 4 primary reports)
    {
      id: "profit-loss",
      title: "Itemized Profit & Loss",
      description: "Menu item profitability, ingredient costs, and net operating margins.",
      path: "/dashboard/reports/profit-loss",
      category: "finance",
      categoryName: "Finance & Accounting",
      icon: <IconCalculator stroke={iconStroke} size={24} />,
      color: "from-emerald-500/10 to-teal-500/10 text-emerald-600 dark:text-emerald-400 border-emerald-200 dark:border-emerald-900/50",
      badgeColor: "bg-emerald-100 text-emerald-800 dark:bg-emerald-950/60 dark:text-emerald-300",
    },
    {
      id: "finance",
      title: "Finance Reports",
      description: "Comprehensive financial overview, daily receipts, and cashflow summary.",
      path: "/dashboard/reports/finance",
      category: "finance",
      categoryName: "Finance & Accounting",
      icon: <IconBuildingBank stroke={iconStroke} size={24} />,
      color: "from-blue-500/10 to-indigo-500/10 text-blue-600 dark:text-blue-400 border-blue-200 dark:border-blue-900/50",
      badgeColor: "bg-blue-100 text-blue-800 dark:bg-blue-950/60 dark:text-blue-300",
    },
    {
      id: "ledger",
      title: "General Ledger",
      description: "Full double-entry ledger accounts, journal entries, and running balances.",
      path: "/dashboard/reports/ledger",
      category: "finance",
      categoryName: "Finance & Accounting",
      icon: <IconChartArea stroke={iconStroke} size={24} />,
      color: "from-indigo-500/10 to-blue-500/10 text-indigo-600 dark:text-indigo-400 border-indigo-200 dark:border-indigo-900/50",
      badgeColor: "bg-indigo-100 text-indigo-800 dark:bg-indigo-950/60 dark:text-indigo-300",
    },
    {
      id: "trial-balance",
      title: "Trial Balance",
      description: "Account balances report ensuring total debits match total credits.",
      path: "/dashboard/reports/trial-balance",
      category: "finance",
      categoryName: "Finance & Accounting",
      icon: <IconScale stroke={iconStroke} size={24} />,
      color: "from-blue-500/10 to-cyan-500/10 text-blue-600 dark:text-blue-400 border-blue-200 dark:border-blue-900/50",
      badgeColor: "bg-blue-100 text-blue-800 dark:bg-blue-950/60 dark:text-blue-300",
    },
    {
      id: "balance-sheet",
      title: "Balance Sheet",
      description: "Financial snapshot of Assets, Liabilities, and Equity at a glance.",
      path: "/dashboard/reports/balance-sheet",
      category: "finance",
      categoryName: "Finance & Accounting",
      icon: <IconBuildingBank stroke={iconStroke} size={24} />,
      color: "from-purple-500/10 to-violet-500/10 text-purple-600 dark:text-purple-400 border-purple-200 dark:border-purple-900/50",
      badgeColor: "bg-purple-100 text-purple-800 dark:bg-purple-950/60 dark:text-purple-300",
    },
    {
      id: "shift-control",
      title: "Shift Control",
      description: "Manage cashier shifts, open/close drawers, and view cash expected vs actual.",
      path: "/dashboard/financial/shifts",
      category: "finance",
      categoryName: "Finance & Accounting",
      icon: <IconCashBanknote stroke={iconStroke} size={24} />,
      color: "from-blue-500/10 to-indigo-500/10 text-blue-600 dark:text-blue-400 border-blue-200 dark:border-blue-900/50",
      badgeColor: "bg-blue-100 text-blue-800 dark:bg-blue-950/60 dark:text-blue-300",
      scopes: [SCOPES.POS, SCOPES.REPORTS]
    },
    {
      id: "register-control",
      title: "Register Control",
      description: "Overview of all shifts and collections for the entire business date.",
      path: "/dashboard/financial/registers",
      category: "finance",
      categoryName: "Finance & Accounting",
      icon: <IconReportAnalytics stroke={iconStroke} size={24} />,
      color: "from-purple-500/10 to-pink-500/10 text-purple-600 dark:text-purple-400 border-purple-200 dark:border-purple-900/50",
      badgeColor: "bg-purple-100 text-purple-800 dark:bg-purple-950/60 dark:text-purple-300",
      scopes: [SCOPES.REPORTS]
    },
    { id: "day-book",
      title: "Day Book Report",
      description: "Cash drawer shift register, daily cash inflow/outflow, and closing logs.",
      path: "/dashboard/financial/daybook",
      category: "finance",
      categoryName: "Finance & Accounting",
      icon: <IconReportMoney stroke={iconStroke} size={24} />,
      color: "from-teal-500/10 to-emerald-500/10 text-teal-600 dark:text-teal-400 border-teal-200 dark:border-teal-900/50",
      badgeColor: "bg-teal-100 text-teal-800 dark:bg-teal-950/60 dark:text-teal-300",
    },

    // 5. Management & Administration
    {
      id: "hr",
      title: "HR Reports",
      description: "Staff attendance, shift hours, tips distribution, and payroll records.",
      path: "/dashboard/reports/hr",
      category: "admin",
      categoryName: "Management",
      icon: <IconBriefcase stroke={iconStroke} size={24} />,
      color: "from-orange-500/10 to-amber-500/10 text-orange-600 dark:text-orange-400 border-orange-200 dark:border-orange-900/50",
      badgeColor: "bg-orange-100 text-orange-800 dark:bg-orange-950/60 dark:text-orange-300",
    },
    {
      id: "audit",
      title: "Audit Reports",
      description: "System security logs, user permission changes, and compliance checks.",
      path: "/dashboard/reports/audit",
      category: "admin",
      categoryName: "Management",
      icon: <IconShieldCheck stroke={iconStroke} size={24} />,
      color: "from-emerald-500/10 to-green-500/10 text-emerald-600 dark:text-emerald-400 border-emerald-200 dark:border-emerald-900/50",
      badgeColor: "bg-emerald-100 text-emerald-800 dark:bg-emerald-950/60 dark:text-emerald-300",
    },
    {
      id: "executive",
      title: "Executive Reports",
      description: "High-level summary of business KPIs, growth trends, and profitability.",
      path: "/dashboard/reports/executive",
      category: "admin",
      categoryName: "Management",
      icon: <IconChartArea stroke={iconStroke} size={24} />,
      color: "from-indigo-500/10 to-purple-500/10 text-indigo-600 dark:text-indigo-400 border-indigo-200 dark:border-indigo-900/50",
      badgeColor: "bg-indigo-100 text-indigo-800 dark:bg-indigo-950/60 dark:text-indigo-300",
    }
  ];

  const categories = [
    { key: "all", label: `All Reports (${reportsList.length})` },
    { key: "hotel", label: "Hotel & PMS (7)" },
    { key: "sales", label: "Sales & Kitchen (5)" },
    { key: "finance", label: "Finance & Accounts (6)" },
    { key: "inventory", label: "Inventory & Supply (2)" },
    { key: "admin", label: "Management & HR (3)" },
  ];

  const filteredReports = useMemo(() => {
    return reportsList.filter((report) => {
      const matchesCategory = selectedCategory === "all" || report.category === selectedCategory;
      const query = searchQuery.trim().toLowerCase();
      const matchesSearch =
        !query ||
        report.title.toLowerCase().includes(query) ||
        report.description.toLowerCase().includes(query) ||
        report.categoryName.toLowerCase().includes(query);
      return matchesCategory && matchesSearch;
    });
  }, [searchQuery, selectedCategory]);

  return (
    <div className="mb-10 print:hidden">
      {/* Search & Category Filter Header */}
      <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4 mb-6">
        <div>
          <div className="flex items-center gap-2">
            <div className="p-2 rounded-xl bg-restro-green/10 text-restro-green">
              <IconLayoutGrid size={22} stroke={iconStroke} />
            </div>
            <div>
              <h2 className="text-2xl font-black text-restro-text">
                Reports & Analytics Directory
              </h2>
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                Browse all operational, hotel, financial, audit, kitchen, and inventory reports in grid view
              </p>
            </div>
          </div>
        </div>

        {/* Search input */}
        <div className="relative w-full lg:w-80">
          <IconSearch
            size={18}
            className="absolute left-3.5 top-1/2 -translate-y-1/2 text-gray-400"
          />
          <input
            type="text"
            placeholder="Search reports by title or metric..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full pl-10 pr-4 py-2.5 rounded-2xl bg-white dark:bg-restro-card-bg border border-restro-border-green text-sm text-restro-text placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-restro-green shadow-sm"
          />
          {searchQuery && (
            <button
              onClick={() => setSearchQuery("")}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-gray-400 hover:text-gray-600"
            >
              Clear
            </button>
          )}
        </div>
      </div>

      {/* Category Pills */}
      <div className="flex items-center gap-2 overflow-x-auto pb-2 scrollbar-none mb-6">
        {categories.map((cat) => (
          <button
            key={cat.key}
            onClick={() => setSelectedCategory(cat.key)}
            className={`px-4 py-2 rounded-full text-xs font-bold transition-all whitespace-nowrap ${
              selectedCategory === cat.key
                ? "bg-restro-green text-white shadow-md shadow-restro-green/20"
                : "bg-white dark:bg-restro-card-bg text-gray-600 dark:text-gray-300 border border-restro-border-green hover:bg-restro-green/5"
            }`}
          >
            {cat.label}
          </button>
        ))}
      </div>

      {/* Grid of Reports Cards */}
      {filteredReports.length === 0 ? (
        <div className="p-12 text-center border border-dashed border-gray-300 dark:border-gray-700 rounded-3xl bg-white/50 dark:bg-restro-card-bg/50">
          <IconFolder size={48} className="mx-auto text-gray-400 mb-3" />
          <p className="text-base font-bold text-gray-700 dark:text-gray-300">
            No reports found matching "{searchQuery}"
          </p>
          <button
            onClick={() => {
              setSearchQuery("");
              setSelectedCategory("all");
            }}
            className="mt-3 text-xs font-bold text-restro-green hover:underline"
          >
            Reset search and filters
          </button>
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
          {filteredReports.map((report) => (
            <Link
              to={report.path}
              key={report.id}
              className="group border border-restro-border-green rounded-2xl p-5 bg-white dark:bg-restro-card-bg hover:shadow-lg hover:-translate-y-0.5 transition-all duration-200 flex flex-col justify-between"
            >
              <div>
                <div className="flex items-start justify-between gap-2 mb-3">
                  <div
                    className={`p-3 rounded-2xl bg-gradient-to-br border ${report.color} group-hover:scale-105 transition-transform`}
                  >
                    {report.icon}
                  </div>
                  <span
                    className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${report.badgeColor}`}
                  >
                    {report.categoryName}
                  </span>
                </div>

                <h4 className="text-base font-black text-restro-text group-hover:text-restro-green transition-colors">
                  {report.title}
                </h4>

                <p className="text-xs text-gray-500 dark:text-gray-400 mt-1.5 leading-relaxed line-clamp-2">
                  {report.description}
                </p>
              </div>

              <div className="mt-4 pt-3 border-t border-gray-100 dark:border-neutral-800 flex items-center justify-between text-xs font-bold text-restro-green group-hover:underline">
                <span>View Report</span>
                <IconArrowRight
                  size={16}
                  className="group-hover:translate-x-1 transition-transform"
                />
              </div>
            </Link>
          ))}
        </div>
      )}
    </div>
  );
}
